// src/components/Inventory.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getInventory, activateInventoryItem, activateRoleItem, extendActiveRole, deleteInventoryItem, transferInventoryItem, sellInventoryItem, getAllUsers } from '../services/api';
import UserSearchInput from './UserSearchInput'; // Наш "умный" поиск
import Modal from './Modal'; // Наше модальное окно
import pointsIcon from '../assets/points_icon.png';
import { processRoleIcon } from '../utils/imageCompressor';
import '../App.css';


// Компонент для одного предмета в инвентаре (InventoryItem)
// Он остается таким же, как в предыдущем ответе.
function InventoryItem({ item, onActivate, activeRole, onDelete, onTransfer, onSell }) {
    const [phase, setPhase] = useState('idle'); // idle -> extending -> replacing -> setting_up -> activating
    const [roleName, setRoleName] = useState('');
    const [roleColor, setRoleColor] = useState('#99aab5');

    const [roleIconBase64, setRoleIconBase64] = useState(null);
    const fileInputRef = useRef(null);

    const isItemVip = item.metadata?.roleId;
    const isActiveRoleVip = activeRole ? activeRole.isCustom === false : null;

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const base64 = await processRoleIcon(file, 128);
                setRoleIconBase64(base64);
            } catch (err) {
                alert(err.message || 'Ошибка обработки картинки');
            }
        }
    };

    // Удаление выбранной иконки
    const handleRemoveIcon = (e) => {
        e.stopPropagation();
        setRoleIconBase64(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmitRole = (e) => {
        e.preventDefault();
        handleAction('activate', {
            roleName,
            roleColor,
            roleIcon: roleIconBase64 // Передаем иконку (или null)
        });
    };

    const handleInitialActivate = () => {
        // Если это не роль (например, XP бустер), активируем сразу
        if (item.type !== 'ROLE') {
            setPhase('activating');
            onActivate(item.id, null, 'activate');
            return;
        }

        if (!activeRole) {
            isItemVip ? handleAction('activate') : setPhase('setting_up');
            return;
        }

        if ((isItemVip && isActiveRoleVip) || (!isItemVip && !isActiveRoleVip)) {
            // Если типы ролей совпадают (VIP->VIP или Кастом->Кастом) -> Продление
            setPhase('extending');
        } else {
            // Если типы ролей РАЗНЫЕ -> Замена
            setPhase('replacing');
        }
    };

    const handleAction = (action, options = null) => {
        setPhase('activating');
        onActivate(item.id, options, action);
    };

    let sellPrice = 0;
    const isSellable = (() => {
        if (item.isSellable === false) return false;
        if (item.type === 'XP_BOOSTER' && item.metadata?.xp) {
            sellPrice = Math.floor(item.metadata.xp * 0.25);
            return true;
        }
        if (item.type === 'ROLE' && item.metadata?.days && !item.metadata?.roleId) {
            sellPrice = item.metadata.days * 75;
            return true;
        }
        return false;
    })();

    const handleSell = () => {
        if (window.confirm(`Вы уверены, что хотите продать "${item.name}" за ${sellPrice} очков?`)) {
            onSell(item.id);
        }
    };

    const resetState = () => setPhase('idle');

    // --- Рендеринг разных состояний (фаз) ---

    if (phase === 'activating') {
        return (
            <div className="inventory-item card">
                <h4>{item.name}</h4>
                <p>Активация...</p>
                <button disabled>Активация...</button>
            </div>
        );
    }

    if (phase === 'extending') {
        // --- ИЗМЕНЕНИЕ: Теперь это окно ТОЛЬКО для продления ---
        return (
            <div className="inventory-item card extend-options">
                <h5>Продлить активную роль?</h5>
                <p>Срок действия вашей текущей роли будет увеличен на {item.metadata.days} дней.</p>
                <div className="extend-actions">
                    <button onClick={() => handleAction('extend')}>Да, продлить</button>
                    {!isItemVip && (
                        <button className="cancel-button" onClick={() => setPhase('replacing')}>Нет, хочу заменить на новую</button>
                    )}
                </div>
                {/* Кнопка "Заменить" убрана, но для кастомных ролей даем возможность создать новую */}

                <button className="cancel-button" onClick={resetState}>Отмена</button>
            </div>
        );
    }

    if (phase === 'replacing') {
        let warningText;
        if (isItemVip && !isActiveRoleVip) {
            warningText = "У вас уже есть активная эксклюзивная роль. Активация VIP-роли удалит вашу эксклюзивную. Продолжить?";
        } else if (!isItemVip && isActiveRoleVip) {
            warningText = "У вас есть активная VIP-роль. Активация эксклюзивной роли удалит VIP. Продолжить?";
        } else {
            warningText = "Вы действительно хотите заменить вашу роль на новую?";
        }

        return (
            <div className="inventory-item card extend-options">
                <h5>Заменить активную роль?</h5>
                <p>{warningText}</p>
                <div className="extend-actions">
                    <button className="danger" onClick={() => isItemVip ? handleAction('activate') : setPhase('setting_up')}>
                        Да, заменить
                    </button>
                </div>
                <button className="cancel-button" onClick={resetState}>Отмена</button>
            </div>
        );
    }

    if (phase === 'setting_up') {
        return (
            <div className="inventory-item card role-setup">
                <h5>Настройка эксклюзивной роли</h5>
                <form onSubmit={handleSubmitRole}>

                    {/* Секция выбора иконки */}
                    <div className="role-icon-uploader">
                        <label>Иконка роли (опционально):</label>
                        <div className="role-icon-preview-box" onClick={() => fileInputRef.current?.click()}>
                            {roleIconBase64 ? (
                                <div className="role-icon-selected">
                                    <img src={roleIconBase64} alt="icon preview" />
                                    <button type="button" className="remove-icon-btn" onClick={handleRemoveIcon}>&times;</button>
                                </div>
                            ) : (
                                <div className="role-icon-placeholder">
                                    <span className="plus-icon">+</span>
                                    <small>Выбрать значок</small>
                                </div>
                            )}
                        </div>
                        {/* Скрытый input выбора файла */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png, image/jpeg, image/webp"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </div>

                    <label>Название роли:</label>
                    <input
                        type="text"
                        value={roleName}
                        onChange={e => setRoleName(e.target.value)}
                        placeholder="Моя крутая роль"
                        required
                    />

                    <label>Цвет роли:</label>
                    <div className="color-picker-row">
                        <input
                            type="color"
                            value={roleColor}
                            onChange={e => setRoleColor(e.target.value)}
                        />
                        <span style={{ color: roleColor, fontWeight: 'bold' }}>{roleColor}</span>
                    </div>

                    <div className="role-setup-actions">
                        <button type="button" onClick={resetState}>Отмена</button>
                        <button type="submit">Создать и активировать</button>
                    </div>
                </form>
            </div>
        );
    }

    // Рендеринг по умолчанию (phase === 'idle')
    return (
        <div className="inventory-item card">
            {/* --- НОВЫЕ КНОПКИ УДАЛЕНИЯ И ПЕРЕДАЧИ --- */}
            <div className="item-actions">
                <button onClick={() => onTransfer(item)} title="Передать предмет">
                    {/* SVG иконки "поделиться" */}
                    <svg viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3z" /></svg>
                </button>
                <button onClick={() => onDelete(item.id)} title="Удалить предмет" className="danger">
                    {/* SVG иконки "корзина" */}
                    <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                </button>
            </div>

            <h4>{item.name}</h4>
            <p>{item.description}</p>
            <div className="item-main-actions">
                <button className="use-button" onClick={handleInitialActivate}>Использовать</button>
                {/* --- НОВАЯ КНОПКА ПРОДАЖИ --- */}
                {isSellable && (
                    <button className="sell-button" onClick={handleSell}>
                        {sellPrice} <img src={pointsIcon} alt="points" className="points-icon" />
                    </button>
                )}
            </div>
        </div>
    );
}


// Основной компонент страницы
export default function Inventory() {
    const [inventoryData, setInventoryData] = useState({ items: [], activeRole: null });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [itemToTransfer, setItemToTransfer] = useState(null);
    const [allUsers, setAllUsers] = useState([]);

    const fetchInventory = () => {
        setLoading(true);
        getInventory().then(res => setInventoryData(res.data)).finally(() => setLoading(false));
    };
    useEffect(() => {
        fetchInventory();
        getAllUsers().then(res => setAllUsers(res.data)); // Загружаем всех юзеров для передачи
    }, []);

    const handleActivateItem = async (itemId, options, action) => {
        setMessage('');
        try {
            const item = inventoryData.items.find(i => i.id === itemId);
            if (!item) throw new Error("Предмет не найден");

            let response;
            if (action === 'extend') {
                response = await extendActiveRole(itemId);
            } else if (item.type === 'ROLE') {
                response = await activateRoleItem({ inventoryItemId: itemId, ...options });
            } else { // 'activate' для XP_BOOSTER
                response = await activateInventoryItem(itemId);
            }

            setMessage(response.data.message);
            fetchInventory(); // Обновляем инвентарь и статус активной роли
        } catch (error) {
            setMessage(error.response?.data?.error || 'Произошла ошибка.');
            // Сбрасываем состояние активации в дочерних компонентах (сложно, но можно через key)
            // Пока просто обновляем инвентарь, чтобы сбросить все
            fetchInventory();
        }
    };
    const handleDeleteItem = async (itemId) => {
        if (window.confirm('Вы уверены, что хотите безвозвратно удалить этот предмет?')) {
            try {
                await deleteInventoryItem(itemId);
                fetchInventory(); // Обновляем список
            } catch (error) { setMessage(error.response?.data?.error || 'Ошибка удаления.'); }
        }
    };
    const openTransferModal = (item) => {
        setItemToTransfer(item);
        setIsTransferModalOpen(true);
    };
    const handleTransferItem = async (recipientId) => {
        // Эта функция будет вызываться из модального окна
        try {
            await transferInventoryItem(itemToTransfer.id, recipientId);
            setIsTransferModalOpen(false);
            fetchInventory();
        } catch (error) {
            // Можно показать ошибку прямо в модальном окне
            alert(error.response?.data?.error || 'Ошибка передачи.');
        }
    };
    const handleSellItem = async (itemId) => {
        try {
            const res = await sellInventoryItem(itemId);
            setMessage(res.data.message);
            fetchInventory(); // Обновляем инвентарь и данные профиля
        } catch (error) {
            setMessage(error.response?.data?.error || 'Ошибка продажи.');
        }
    };

    if (loading) {
        return <div className="loading-screen">Загрузка инвентаря...</div>;
    }

    return (
        <div className="inventory-page">
            <Link to="/" className="back-link">&larr; Назад на главную</Link>
            <h1>Инвентарь</h1>
            {message && <p className="inventory-message">{message}</p>}

            <div className="active-role-section">
                <h2>Активная роль</h2>
                {inventoryData.activeRole ? (
                    <p>Ваша текущая кастомная/VIP роль истекает: {new Date(inventoryData.activeRole.expiresAt).toLocaleString('ru-RU')}</p>
                ) : (
                    <p>У вас нет активных ролей, полученных через сервис.</p>
                )}
                <h2>Статус PRO-Бустера</h2>
                {inventoryData.activeBooster ? (
                    <div className="active-booster-card">
                        <p>⚡ <strong>PRO-Бустер активен! (x2 XP)</strong></p>
                        <p>Истекает: {new Date(inventoryData.activeBooster.expiresAt).toLocaleString('ru-RU')}</p>
                    </div>
                ) : (
                    <p>У вас нет активного PRO-Бустера.</p>
                )}
            </div>

            <div className="inventory-items-section">
                <h2>Ваши предметы</h2>
                {inventoryData.items.length > 0 ? (
                    <div className="inventory-grid">
                        {inventoryData.items.map(item => (
                            <InventoryItem
                                key={item.id}
                                item={item}
                                onActivate={handleActivateItem}
                                activeRole={inventoryData.activeRole}
                                onDelete={handleDeleteItem}
                                onTransfer={openTransferModal}
                                onSell={handleSellItem}
                            />
                        ))}
                    </div>
                ) : (
                    <p>У вас пока нет неактивированных предметов.</p>
                )}
            </div>
            <Modal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} size="narrow">
                <TransferItemModal
                    item={itemToTransfer}
                    users={allUsers}
                    onTransfer={handleTransferItem}
                />
            </Modal>
        </div>
    );
}
function TransferItemModal({ item, users, onTransfer }) {
    const [recipientId, setRecipientId] = useState('');

    if (!item) return null;

    return (
        <div className="transfer-item-modal">
            <h3>Передача предмета</h3>
            <div className="item-to-transfer">{item.name}</div>
            <UserSearchInput
                users={users}
                onUserSelect={setRecipientId}
                placeholder="Выберите получателя"
            />
            <button
                onClick={() => onTransfer(recipientId)}
                disabled={!recipientId}
                className="confirm-transfer-btn"
            >
                Передать
            </button>
        </div>
    );
}